import { useRef, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { useDataStore } from "@store/user_org";
import {
  signatureRequestsApi,
  InlineSignatureField,
} from "@services/api/signatureRequests";
import { showNotification } from "context/notification.service";
import { Project, SignatureRequest } from "types";
import {
  SignerSelectionList,
  SelectedSigner,
} from "@components/ui/blocks/SignerSelectionList";
import type { FieldData } from "@components/modals/signature";
import {
  PlacementMode,
  PlacementModeSelector,
  InlineFieldsSummary,
  PlaceFieldsOverlay,
} from "@components/modals/signature/PlacementModeControls";

// Stable palette used to colour signer chips across the editor + summary.
const SIGNER_COLORS = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-teal-500",
];

export const SignatureSetupModal = ({
  onClose,
  project,
  fileId,
  pdfUrl,
  documentName,
  onComplete,
}: {
  onClose: () => void;
  project: Project;
  fileId: string;
  pdfUrl?: string;
  documentName: string;
  onComplete?: (newRequest?: SignatureRequest) => void;
}) => {
  const orgData = useDataStore((state) => state.orgData);
  const [signers, setSigners] = useState<SelectedSigner[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [placementMode, setPlacementMode] = useState<PlacementMode>("appended_page");
  const [inlineFields, setInlineFields] = useState<FieldData[]>([]);
  const [inlineDimensions, setInlineDimensions] = useState<{ width: number; height: number } | null>(null);
  const [placeFieldsOpen, setPlaceFieldsOpen] = useState(false);
  // Synchronous lock against double-click. React state is async so a
  // fast second click can slip past `disabled={isSubmitting}` before the
  // render commits.
  const submitLockRef = useRef(false);

  const handleSubmit = async () => {
    if (signers.length === 0) {
      showNotification({ type: "error", message: "Sélectionnez au moins un signataire" });
      return;
    }
    if (placementMode === "inline_fields") {
      if (inlineFields.length === 0 || !inlineDimensions) {
        showNotification({
          type: "error",
          message: "Placez au moins un champ sur le document.",
        });
        return;
      }
      // Every signer must have at least one block on the PDF.
      const missing = signers.filter(
        (s) => !inlineFields.some((f) => f.signer_id === s.id),
      );
      if (missing.length > 0) {
        showNotification({
          type: "error",
          message: `Signataire(s) sans champ : ${missing
            .map((m) => m.signer_name)
            .join(", ")}`,
        });
        return;
      }
    }
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setIsSubmitting(true);
    try {
      const apiFields: InlineSignatureField[] | undefined =
        placementMode === "inline_fields"
          ? inlineFields.map((f) => ({
              type: f.type as InlineSignatureField["type"],
              x: f.left,
              y: f.top,
              width: f.width,
              height: f.height,
              page: f.page,
              label: f.name,
              content: f.content,
              signer_id: f.signer_id,
            }))
          : undefined;

      // eslint-disable-next-line no-console
      console.log("[sig.create] sending", {
        project_id: project.id,
        document_name: documentName,
        file_id: fileId,
        placement_mode: placementMode,
        fields_count: apiFields?.length ?? 0,
      });

      const newRequest = await signatureRequestsApi.create({
        project_id: project.id,
        org_id: orgData?.id || "",
        document_name: documentName,
        file_id: fileId,
        signers: signers.map((s) => ({
          id: s.id,
          signer_name: s.signer_name,
          signer_email: s.signer_email,
          signer_type: s.signer_type,
          initials: s.initials || undefined,
        })),
        placement_mode: placementMode,
        fields: apiFields,
        target_width: inlineDimensions?.width,
        target_height: inlineDimensions?.height,
      });

      showNotification({
        type: "success",
        message: "Document envoyé pour signature",
      });
      onComplete?.(newRequest);
      if (!onComplete) onClose();
    } catch (error) {
      console.error("Error creating signature request:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de l'envoi pour signature",
      });
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };

  // "Placer les champs" is only reachable when the document has a URL we
  // can render. If pdfUrl isn't forwarded we stay on appended_page mode.
  const canUseInlineMode = !!pdfUrl;

  // Shape the signers list the way the placement editor expects.
  // We pass `initials` through so the sidebar can display + edit them.
  const signerOptions = signers.map((s, i) => ({
    id: s.id,
    name: s.signer_name || s.signer_email || `Signataire ${i + 1}`,
    color: SIGNER_COLORS[i % SIGNER_COLORS.length],
    initials: s.initials,
  }));

  const handleSignerInitialsChange = (signerId: string, initials: string) => {
    setSigners((prev) =>
      prev.map((s) => (s.id === signerId ? { ...s, initials } : s)),
    );
  };
  const perSignerCounts = signerOptions.map((s) => ({
    id: s.id,
    name: s.name,
    count: inlineFields.filter((f) => f.signer_id === s.id).length,
  }));

  return (
    <>
      <PlaceFieldsOverlay
        open={placeFieldsOpen}
        pdfUrl={pdfUrl ?? null}
        documentName={documentName}
        initialFields={inlineFields}
        initialDimensions={inlineDimensions}
        signers={signerOptions}
        onClose={() => setPlaceFieldsOpen(false)}
        onConfirm={(fields, dimensions) => {
          setInlineFields(fields);
          setInlineDimensions(dimensions);
          setPlaceFieldsOpen(false);
        }}
        onSignerInitialsChange={handleSignerInitialsChange}
      />
      <BaseModal
        title="Signature électronique"
        startIcon={<Icon1 name="sign" size={20} />}
        onClose={onClose}
      >
        <div className="flex flex-col gap-4 p-4">
          <div>
            <div className="text-h2 font-semibold mb-1">
              Qui doit signer ce document ?
            </div>
            <div className="text-h3 text-neutral-400">{documentName}</div>
          </div>

          <PlacementModeSelector
            mode={placementMode}
            onChange={setPlacementMode}
            inlineDisabled={!canUseInlineMode}
            inlineDisabledReason="Le mode « Champs dans le document » nécessite un aperçu PDF disponible."
          />

          <SignerSelectionList
            project={project}
            signers={signers}
            setSigners={setSigners}
          />

          {placementMode === "inline_fields" && (
            <InlineFieldsSummary
              count={inlineFields.length}
              disabled={!pdfUrl || signers.length === 0}
              onEdit={() => setPlaceFieldsOpen(true)}
              onClear={() => {
                setInlineFields([]);
                setInlineDimensions(null);
              }}
              perSignerCounts={perSignerCounts}
            />
          )}
        </div>

        <div className="border-t p-4 flex justify-end gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Annuler
          </SecondaryButton>
          <Button
            variant="small"
            label={isSubmitting ? "Envoi en cours..." : "Envoyer pour signature"}
            onClick={handleSubmit}
            disabled={isSubmitting || signers.length === 0}
          />
        </div>
      </BaseModal>
    </>
  );
};
